import { defineStore } from 'pinia'
import axios from 'axios' // Ensure axios is installed and configured

/**
 * Creates the base state for a generic resource store.
 * @param {string} apiEndpoint - The base API endpoint for the resource.
 */
const createBaseState = (apiEndpoint) => ({
  items: [],
  itemsSelected: [],
  total: null,
  perPage: 20,
  page: 1,
  lastPage: 1,
  orderBy: 'created_at',
  orderDirection: 'desc',
  api: apiEndpoint,
  isLoading: false,
  isLoadingItems: [],
  error: null,
  searchQuery: null,
  filterValue: null,
  filterByField: null
})

/**
 * Base getters for the generic store.
 */
const baseGetters = {
  getItemById(state) {
    return (itemId) => state.items.find(item => item.id === itemId)
  },
  getItemByUuId(state) {
    return (itemUuid) => state.items.find(item => item.uuid === itemUuid)
  },
  getItems(state) {
    return state.items
  }
}

/**
 * Base actions for the generic store, including all CRUD operations.
 */
const baseActions = {
  // --- LOCAL STATE ACTIONS ---

  sortChanged(params) {
    this.page = 1
    this.orderBy = params.orderBy
    this.orderDirection = params.orderDirection
  },

  filtersChanged(params) {
    this.page = 1
    this.filterByField = params.field || null
    this.filterValue = params.value || null
  },

  searchChanged(searchQuery) {
    this.page = 1
    this.searchQuery = searchQuery || null
  },

  /**
   * Updates or adds an item to the local 'items' array.
   * @param {object} item - The item to add or update.
   */
  itemPatchState(item) {
    const index = this.items.findIndex(itemLoop => itemLoop.id === item.id)
    if (index > -1) {
      this.items.splice(index, 1, item)
    } else {
      this.items.push(item)
    }
  },

  /**
   * Removes an item from the local 'items' array by its ID.
   * @param {number|string} itemId - The ID of the item to remove.
   */
  itemRemoveFromState(itemId) {
    const index = this.items.findIndex(itemLoop => itemLoop.id === itemId)
    if (index > -1) {
      this.items.splice(index, 1)
    }
  },

  /**
   * Clears all items and resets pagination.
   */
  clearItems() {
    this.items = []
    this.total = null
    this.page = 1
    this.lastPage = 1
  },

  // --- ASYNC API ACTIONS ---

  /**
   * Fetches a single item by its ID and patches it into the state.
   * @param {number|string} itemId - The ID of the item to fetch.
   */
  async fetchItemById(itemId) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await axios.get(`${this.api}/${itemId}`)
      this.itemPatchState(data)
      return data
    } catch (err) {
      this.error = err
      console.error(`Error fetching item ${itemId}:`, err)
      throw err // Re-throw for component to handle
    } finally {
      this.isLoading = false
    }
  },

  /**
   * Fetches a list of items, merges them, and updates pagination state.
   * @param {object} params - Query parameters (e.g., { page: 1, clear: true }).
   */
  async fetchItems(params) {
    this.isLoading = true
    this.error = null
    const paramsInitial = {
      page: this.page,
      per_page: this.perPage,
      order_by: this.orderBy,
      order_direction: this.orderDirection,
      clear: true,
      ...this.filterByField ? { search_by_field: this.filterByField } : {},
      ...this.filterValue ? { search: this.filterValue } : {},
      ...this.searchQuery ? { search: this.searchQuery } : {}
    }
    params = { ...paramsInitial, ...params }

    try {
      const { data } = await axios.get(this.api, { params })

      // Handle data (assuming Laravel pagination structure)
      const items = data.data || data; // Support non-paginated arrays

      if (params && params.clear) {
        this.items = items
      } else {
        for (const item of items) {
          this.itemPatchState(item)
        }
      }

      // Update pagination state if data is paginated
      if (data.current_page) {
        // Standard pagination
        this.page = data.current_page
        this.perPage = data.per_page
        this.total = data.total
        this.lastPage = data.last_page
      }
      else if(data.meta) {
        // API paginated resource
        this.page = data.meta.current_page
        this.perPage = data.meta.per_page
        this.total = data.meta.total
        this.lastPage = data.meta.last_page
      }

      return data
    } catch (err) {
      this.error = err
      console.error('Error fetching items:', err)
      throw err
    } finally {
      this.isLoading = false
    }
  },

  /**
   * Creates a new item via API and patches it into the state.
   * @param {object} payload - The data for the new item.
   */
  async itemCreate(payload) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await axios.post(this.api, payload)
      this.itemPatchState(data)
      return data
    } catch (err) {
      this.error = err
      console.error('Error creating item:', err)
      throw err
    } finally {
      this.isLoading = false
    }
  },

  /**
   * Updates an item via API and patches the new data into the state.
   * @param {number|string} id - The ID of the item to patch.
   * @param {object} payload - The data to update.
   */
  async itemPatch(id, payload) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await axios.patch(`${this.api}/${id}`, payload)
      this.itemPatchState(data)
      return data
    } catch (err) {
      this.error = err
      console.error(`Error patching item ${id}:`, err)
      throw err
    } finally {
      this.isLoading = false
    }
  },

  /**
   * Deletes an item via API and removes it from the state.
   * @param {number|string} itemId - The ID of the item to delete.
   */
  async itemDelete(itemId) {
    this.isLoadingItems.push(itemId)
    this.error = null
    try {
      const { data } = await axios.delete(`${this.api}/${itemId}`)
      this.itemRemoveFromState(itemId)
      return data
    } catch (err) {
      this.error = err
      console.error(`Error deleting item ${itemId}:`, err)
      throw err
    } finally {
      this.isLoadingItems = this.isLoadingItems.filter(id => id !== itemId)
    }
  },

  /**
   * Restores a soft-deleted item (Laravel-specific).
   * @param {number|string} itemId - The ID of the item to restore.
   */
  async itemRestore(itemId) {
    this.isLoading = true
    this.error = null
    try {
      // Assumes a Laravel standard restore endpoint
      const { data } = await axios.post(`${this.api}/${itemId}/restore`)
      this.itemPatchState(data) // Patch the restored item back into state
      return data
    } catch (err) {
      this.error = err
      console.error(`Error restoring item ${itemId}:`, err)
      throw err
    } finally {
      this.isLoading = false
    }
  }
}

// --- THE FACTORY ---

/**
 * Creates a generic, dynamic Pinia store factory for a RESTful resource.
 *
 * @param {string} baseStoreId - The base ID for the store (e.g., 'users').
 * @param {string} apiEndpoint - The base API endpoint (e.g., '/api/v1/users').
 * @param {object} [customOptions={}] - Custom state, getters, and actions to merge.
 * @returns {function(string=): Store} A hook function (e.g., useUserStore) that
 * accepts an instanceId (e.g., 'default' or 'adminList').
 */
export const createGenericStore = (baseStoreId, apiEndpoint, customOptions = {}) => {

  // This is the hook function (e.g., useUserStore) that components will import.
  const useStore = (instanceId = 'default') => {

    const {
      state: customState,
      getters: customGetters,
      actions: customActions
    } = customOptions

    // Create the unique, dynamic store ID, e.g., "users_default" or "posts_componentA"
    const dynamicStoreId = `${baseStoreId}_${instanceId}`

    // Use defineStore to create/get the store definition
    // Pinia handles caching, so this definition is created only once per dynamicStoreId
    const storeDefinition = defineStore(dynamicStoreId, {
      state: () => ({
        ...createBaseState(apiEndpoint),
        // Custom state must be a function to be properly reactive
        ...(typeof customState === 'function' ? customState() : {})
      }),
      getters: {
        ...baseGetters,
        ...customGetters
      },
      actions: {
        ...baseActions,
        ...customActions
      }
    })

    // Return the actual store instance for the component to use.
    return storeDefinition()
  }

  // The factory returns the hook function itself.
  return useStore
}
